<template>
    <div class="">
        <!-- xs to lg -->
        <div class="mx-auto mt-12 max-w-2xl space-y-8 sm:mt-16 lg:hidden">
            <div v-for="column in columns" :key="column.name">
                <Column :column="column" :is-pricing="isPricingTable" :hide-price="hidePrice" :hide-cta="hideCta"
                    :show-description="false" @column-click="handleColumnClick">
                    <template #features>
                        <div class="mt-8">
                            <div v-for="(section, sIndex) in sections" :key="sIndex" class="mb-8">
                                <!-- Section Header -->
                                <h4 class="text-sm font-medium text-white mb-4 text-center"> {{ section.title }}</h4>

                                <!-- Features List -->
                                <ul class="space-y-4">
                                    <li v-for="row in section.rows" :key="row.feature"
                                        class="flex justify-between items-start text-sm gap-4">
                                        <span class="text-gray-400 shrink-0 basis-1/2 text-xs">{{ row.feature }}</span>

                                        <span class="sm:flex items-center justify-end basis-1/2">
                                            <Ratings v-if="row.ratings?.[column.key] !== undefined"
                                                :rating="row.ratings?.[column.key]" />


                                            <!-- Yes value -->
                                            <Icon v-if="row.text?.[column.key] === 'Yes'"
                                                name="material-symbols:check-circle"
                                                :class="icon_class || 'text-xl text-blue-500'" />

                                            <!-- Not value -->
                                            <Icon v-else-if="row.text?.[column.key] === 'Not'"
                                                name="material-symbols:cancel" class="text-xl text-red-500" />

                                            <!-- Dash value -->
                                            <span v-else-if="row.text?.[column.key] === '-'"
                                                class="text-xl text-gray-500">-</span>


                                            <!-- String/numeric value -->


                                            <!-- <Icon v-if="row.checked?.[column.key] === true"
                                                    name="lineicons:checkmark-circle"
                                                    class="text-xl text-green-10 font-bold" />
                                                <Icon v-if="row.checked?.[column.key] === false"
                                                    name="lineicons:xmark-circle"
                                                    class="text-xl text-red-10 font-bold" /> -->

                                            <span v-else class="text-gray-400 text-xs"
                                                :class="{ 'text-right': !isTextLeftAligned, 'text-left': isTextLeftAligned }">

                                                {{ row.text?.[column.key] }}


                                            </span>

                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>
                </Column>
            </div>
        </div>

        <!-- lg+ -->
        <div class="isolate mt-4 hidden lg:block px-8 xl:px-8">
            <div class="relative -mx-8">
                <!-- Highlight for specific column -->
                <template v-if="highlightColumnIndex !== null">
                    <div class="absolute inset-x-4 inset-y-0 -z-10 flex">
                        <div class="flex pr-2" :class="columnWidthClass"
                            :style="{ marginLeft: `${(highlightColumnIndex + 1) * (100 / (columns.length + 1))}%` }"
                            aria-hidden="true">
                            <div
                                class="w-full rounded-t-xl border-x border-t border-gray-700/50 bg-gradient-to-br from-gray-900 via-gray-900/95 to-blue-950/10">
                            </div>
                        </div>
                    </div>
                </template>

                <table class="w-full table-fixed border-separate border-spacing-x-2 text-left">
                    <caption class="sr-only">
                        Comparison Table
                    </caption>
                    <colgroup>
                        <col :class="columnWidthClass" v-for="index in columns.length + 1" :key="index">
                    </colgroup>
                    <thead>
                        <tr class="relative">
                            <td></td>
                            <th v-for="column in columns" :key="column.name" scope="col" class="p-6 xl:p-8">
                                <!-- Column Name -->
                                <div class="flex flex-col justify-between text-lg/8 font-bold text-white mb-4"
                                    :class="{ 'min-h-36': column.subtitle }">
                                    <div class="flex flex-col justify-between text-center">
                                        <div>{{ column.name }}</div>
                                        <div v-if="column.subtitle" class="text-sm font-semibold text-white">
                                            {{ column.subtitle }}
                                        </div>
                                    </div>
                                    <div v-if="column.images" class="flex gap-4">
                                        <img v-for="(image, index) in column.images" :key="index" :src="image"
                                            :alt="`${column.name} logo ${index + 1}`"
                                            class="h-12 w-auto max-w-12 object-contain rounded-md" />
                                    </div>
                                </div>
                                <!-- Price Row & CTA Button - Only show if isPricingTable is true -->
                                <template v-if="isPricingTable && !hidePrice && column.price !== undefined">
                                    <p class="mt-2 flex items-baseline gap-x-1 justify-center">
                                        <span class="text-4xl font-semibold tracking-tight text-white">
                                            {{ typeof column.price === 'number'
                                                ? column.price === 0
                                                    ? 'Free'
                                                    : '$' + column.price.toLocaleString('en-US', {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 0
                                                    }) + '+'
                                                : column.price }}
                                        </span>
                                        <span v-if="column.priceUnit && column.price !== 0"
                                            class="text-sm font-semibold text-gray-400">{{
                                                column.priceUnit }}</span>
                                    </p>
                                    <!-- CTA Button -->
                                    <a v-if="!hideCta && column.cta" :data-action="'click-' + column.name.toLowerCase()"
                                        @click.prevent="handleColumnClick(column)"
                                        class="mt-8 block rounded-md px-3 py-2 text-center text-sm/6 font-semibold cursor-pointer"
                                        :class="[
                                            column.isHighlighted
                                                ? 'bg-blue-500 text-white hover:bg-blue-400'
                                                : 'text-blue-400 ring-1 ring-inset ring-blue-500/20 hover:ring-blue-500'
                                        ]">
                                        {{ column.cta }}
                                    </a>
                                </template>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="relative">
                        <template v-for="section in sections" :key="section.title">
                            <!-- Section Header -->
                            <tr v-if="sections.length > 1">
                                <th scope="colgroup" colspan="6" class="py-4 text-lg font-semibold text-white">
                                    {{ section.title }}
                                    <div class="absolute left-0 right-0 bottom-0 h-px bg-gray-700/50"></div>
                                </th>
                            </tr>

                            <!-- Section Rows -->
                            <tr v-for="row in section.rows" :key="row.feature" class="relative">
                                <th scope="row" class="py-4 text-sm/6 font-normal text-white">
                                    <div class="flex items-center gap-x-3">
                                        {{ row.feature }}
                                        <Tooltip v-if="row.tooltip" :text="row.tooltip" />
                                    </div>
                                </th>
                                <td v-for="column in columns" :key="column.name" class="px-6 py-2 xl:px-8"
                                    :class="{ 'text-center': !isTextLeftAligned, 'text-left': isTextLeftAligned }">

                                    <Ratings v-if="row.ratings?.[column.key] !== undefined"
                                        :rating="row.ratings?.[column.key]" />

                                    <template v-if="row.text?.[column.key] === 'Yes'">
                                        <Icon name="material-symbols:check-circle"
                                            :class="icon_class || 'text-xl text-blue-500'" />
                                        <span class="sr-only">Included</span>
                                    </template>
                                    <template v-else-if="row.text?.[column.key] === 'Not'">
                                        <Icon name="material-symbols:cancel" class="text-xl text-red-500" />
                                        <span class="sr-only">Not included</span>
                                    </template>
                                    <template v-else-if="row.text?.[column.key] === '-'">
                                        <span class="text-xl text-gray-500">-</span>
                                        <span class="sr-only">Not included</span>
                                    </template>
                                    <div v-else
                                        :class="{ 'text-center': !isTextLeftAligned, 'text-left': isTextLeftAligned }"
                                        class="py-2 text-sm text-gray-400">

                                        {{ row.text?.[column.key] }}
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { type IColumn } from './Column.vue';

export interface IComparisonFeatureRow {
    feature: string;
    tooltip?: string;
    text?: Record<string, string>;
    ratings?: Record<string, number>;
}

export interface IComparisonSection {
    title: string;
    rows: IComparisonFeatureRow[];
}

export interface IColumnWithKey extends IColumn {
    key: string;
}

export default {
    props: {
        columns: {
            type: Array as PropType<IColumnWithKey[]>,
            required: true
        },
        sections: {
            type: Array as PropType<IComparisonSection[]>,
            required: true
        },
        hidePrice: {
            type: Boolean,
            default: false
        },
        hideCta: {
            type: Boolean,
            default: false
        },
        isPricingTable: {
            type: Boolean,
            default: false
        },
        highlightColumnIndex: {
            type: Number,
            default: null
        },
        isTextLeftAligned: {
            type: Boolean,
            default: false
        },
        icon_class: String
    },

    emits: ['columnClick'],

    computed: {
        columnWidthClass(): string {
            const totalColumns = this.columns.length + 1;
            switch (totalColumns) {
                case 3: return 'w-1/3';
                case 4: return 'w-1/4';
                case 5: return 'w-1/5';
                case 6: return 'w-1/6';
                default: return 'w-1/4';
            }
        }
    },

    methods: {
        // getColumnKey(column: IColumnWithKey): string {
        //     return column.key;
        // },

        handleColumnClick(column: IColumnWithKey) {
            this.$emit('columnClick', column);
        }
    }
};
</script>

<style scoped>
/* Add these styles to handle the borders */
tbody tr {
    position: relative;
}

tbody tr::after,
thead tr::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: rgb(55 65 81 / 0.5);
    /* gray-700/50 */
}
</style>